/**
 * The rotating search placeholder.
 *
 * Draws "Shop" plus a quoted term over the search field, cycling the term, and
 * gets out of the way the moment anyone types.
 *
 * It is an overlay rather than a placeholder attribute for one reason: a
 * placeholder is a single string in a single colour, and this needs two. The
 * input keeps its own placeholder underneath, which is what a screen reader
 * reads and what shows if this script never runs. Nothing here is load-bearing.
 */
if (!customElements.get('search-suggest')) {
  customElements.define(
    'search-suggest',
    class SearchSuggest extends HTMLElement {
      /**
       * connectedCallback runs on every insertion, not once per element, and
       * the header search really does move: the drawer and the sticky header
       * both re-parent it. Wiring twice would leave two timers cycling the same
       * terms at different offsets.
       */
      connectedCallback() {
        if (this.wired) return
        this.wired = true

        this.field = this.closest('.search__field')
        this.input = this.field && this.field.querySelector('.search__input')
        this.terms = [...this.querySelectorAll('.search__suggest-term')]
        if (!this.input || this.terms.length === 0) return

        this.index = 0
        this.onInput = this.sync.bind(this)
        this.input.addEventListener('input', this.onInput)
        this.input.addEventListener('focus', this.onFocus.bind(this))
        this.input.addEventListener('blur', this.onBlur.bind(this))

        /* Take the native placeholder off the input rather than trying to paint
           it transparent from a stylesheet. Two strings were drawing in the same
           spot, and which rule wins the placeholder colour depends on where the
           field is used. The visually-hidden label above still names the field
           for a screen reader, and a browser that never runs this script never
           reaches this line, so it keeps its placeholder. */
        this.nativePlaceholder = this.input.getAttribute('placeholder')
        /* A single space, never an empty string. The theme decides "the
           shopper has typed" from :placeholder-shown, and Safari treats an
           empty placeholder attribute as no placeholder at all: the field
           then reads as typed-in, the magnifier is hidden and the Clear
           button shown before anyone touches it (owner recording,
           2026-09-02). Chromium counts an empty placeholder as shown, which
           is why the desktop never showed it. A space draws nothing and is
           a placeholder in both engines. */
        this.input.setAttribute('placeholder', ' ')

        /* The overlay has to start where the input's text starts, and the input
           is not at the start of the field: a category select sits before it.
           Measured rather than guessed, and redone on resize, because that
           select's width depends on its longest option. */
        this.restyle()
        this.onResize = this.restyle.bind(this)
        window.addEventListener('resize', this.onResize)
        /* The header search opens and closes with a transition on iOS and
           the field's boxes are mid-flight when focus and blur fire; a
           measure taken then is wrong until the next resize. Measure again
           once the transition has settled. */
        this.settle = () => { clearTimeout(this.settleTimer); this.settleTimer = setTimeout(() => this.restyle(), 450) }

        this.sync()
        if (this.terms.length > 1) this.resume()
      }

      disconnectedCallback() {
        this.pause()
        window.removeEventListener('resize', this.onResize)
      }

      /**
       * Position and colour, both read off the input rather than assumed.
       *
       * The colour has to be measured. The field's own tokens say black text on
       * white, and the header paints it white on black, so anything derived
       * from a token here is a guess at which rule won. The one thing that is
       * always right is the colour the shopper's own typing comes out in.
       */
      restyle() {
        this.style.setProperty('--search-suggest-color', getComputedStyle(this.input).color)

        const field = this.field.getBoundingClientRect()
        const input = this.input.getBoundingClientRect()
        const cs = getComputedStyle(this.input)
        const rtl = getComputedStyle(this.field).direction === 'rtl'
        const edge = rtl ? field.right - input.right : input.left - field.left
        const pad = parseFloat(rtl ? cs.paddingRight : cs.paddingLeft) || 0
        /* The input always begins at the field's edge here (the category
           control sits on top of it), so anything but a small edge is a box
           measured mid-transition. Clamp: the start can never pass the
           middle of the field, which is what emptied the overlay after a
           cancelled search on the owner's phone (its max-width is derived
           from this start). */
        const limit = field.width > 0 ? field.width / 2 : Infinity
        const start = Math.min(Math.max(pad + (Math.abs(edge) < 40 ? edge : 0), 0), limit)
        this.style.setProperty('--search-suggest-start', `${Math.round(start)}px`)
      }

      /** Anything typed, and the placeholder stands down. */
      sync() {
        this.hidden = this.input.value.trim().length > 0
      }

      /* The theme recolours the input on focus, so the overlay has to follow. */
      onFocus() {
        this.pause()
        this.restyle()
        this.settle()
      }

      onBlur() {
        this.sync()
        this.restyle()
        this.settle()
        this.resume()
      }

      pause() {
        if (this.timer) clearInterval(this.timer)
        this.timer = null
      }

      /* Rotation stops while the field has focus. A word changing under
         someone's caret while they are deciding what to type is a distraction,
         not a suggestion. */
      resume() {
        this.pause()
        if (this.terms.length < 2) return
        this.timer = setInterval(this.next.bind(this), 2600)
      }

      next() {
        if (this.hidden) return
        const current = this.terms[this.index]
        this.index = (this.index + 1) % this.terms.length
        const incoming = this.terms[this.index]
        current.hidden = true
        incoming.hidden = false
        /* Restart the fade rather than let it run once and never again. */
        incoming.classList.remove('is-entering')
        void incoming.offsetWidth
        incoming.classList.add('is-entering')
      }
    }
  )
}
